import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { useNavigate } from "react-router-dom";
import { View, YStack, Text, Sheet, Form, XStack, Button, Circle, Progress, ScrollView } from "tamagui";
import { RiArrowLeftSLine, RiStarFill } from "react-icons/ri";
import TextCustom, { TextSize } from "@components/TextCoustom";
import AnimNum from "../HomePage/components/AnimNum";
import { useState } from "react";
import FormItem from "@components/form/FormItem";
import { useForm, FormProvider, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { RiCoinsLine, RiArrowDropRightLine, RiCloseLine } from "react-icons/ri";
import TextSpaceBetween from "@components/layout/TextSpaceBetween";
import { ORDER_ENTRY_PATH } from "@screen/Kiosk/orderEntry";

export const TopUp = () => {
  const navigate = useNavigate();
  const prevPathname = "/account/wallet";
  const currentAmount = 2000;
  const progress = Math.min((currentAmount / 5000) * 100, 100);
  const [amount, setAmount] = useState(1000);
  const [method, setMethod] = useState("card");
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);

  //@ 建立 Form
  const formSchema = z.object({
    topUp: z.string().trim().min(1, { message: "請選擇儲值金" }),
  });
  type FormData = z.infer<typeof formSchema>;

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      topUp: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  //@ 處理滾動至錯誤位置
  const onInvalid = (errors: FieldErrors<FormData>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        const fallbackElement = document.querySelector(`[id*="${firstErrorKey}"]`);
        fallbackElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    navigate(ORDER_ENTRY_PATH);
  };

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack alignItems="center" onPress={() => navigate(prevPathname)}>
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <TextCustom str="儲值" type={TextSize.SMALL} color={colors.light} bold={true} />
        </XStack>
        <YStack gap="$2" f={1} m="$3">
          <Text color="rgba(255,255,255,0.75)" fow="500" fontSize={14}>
            儲值餘額
          </Text>
          <AnimNum value={2000} color="white" size={24} unit="NT$" />
        </YStack>
      </Header>
      <RadiusContentWrapper>
        <YStack bg="#FEF3C7" br="$4" p="$4" borderWidth={1} borderColor="#FCD34D">
          <XStack ai="center" gap="$3" mb="$3">
            <Circle size={36} bg="rgba(255,255,255,0.6)" ai="center" jc="center">
              <RiStarFill color={colors.amberDark} size={18} />
            </Circle>
            <YStack f={1}>
              <Text fow="700" color="#78350F" fontSize={13}>
                {currentAmount >= 5000 ? "已達成最高 8% 回饋" : "解鎖更多加贈回饋"}
              </Text>
              <Text color="#92400E" fontSize={11} mt="$0.5">
                本次儲值預計加贈 ${Math.floor(currentAmount * 0.05)}
              </Text>
            </YStack>
          </XStack>
          <Progress value={progress} h={6} bg="rgba(180,83,9,0.15)">
            <Progress.Indicator bg="#F59E0B" animation="bouncy" />
          </Progress>

          <XStack jc="space-between" mt="$2">
            <Text fontSize={10} color="#92400E" fow="600">
              $1000 +$50
            </Text>
            <Text fontSize={10} color="#92400E" fow="600">
              $2000 +5%
            </Text>
            <Text fontSize={10} color="#92400E" fow="600">
              $5000 +8%
            </Text>
          </XStack>
        </YStack>
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit, onInvalid)} gap="$4">
            {[
              {
                itemType: "radio",
                required: false,
                data: {
                  id: "topUp",
                  label: "選擇儲值金額",
                  placeholder: "",
                  value: "",
                  otherInfo: "",
                  options: [
                    {
                      label: "$500",
                      value: "500",
                    },
                    {
                      label: "$1,000",
                      value: "1000",
                    },
                    {
                      label: "$2,000",
                      value: "2000",
                    },
                    {
                      label: "$3,000",
                      value: "3000",
                    },
                    {
                      label: "$5,000",
                      value: "5000",
                    },
                    {
                      label: "$10,000",
                      value: "10000",
                    },
                  ],
                },
              },
            ].map((item) => (
              <YStack key={item.data?.id} id={item.data?.id}>
                <FormItem itemType={item?.itemType} required={item?.required} data={item?.data} />
              </YStack>
            ))}
            <YStack gap={10}>
              <Text fontSize={16} fontWeight="bold">
                支付方式
              </Text>
              <RadiusCard>
                <XStack alignItems="center" justifyContent="space-between" onPress={() => setShowPaymentSheet(true)}>
                  <RadiusBoxLabel label="支付方式" icon={<RiCoinsLine color={colors.primary} />} />
                  <XStack alignItems="center">
                    <Text color={colors.gray} fontSize={14} fontWeight="bold">
                      變更
                    </Text>
                    <RiArrowDropRightLine color={colors.gray} size={24} />
                  </XStack>
                </XStack>
                <Sheet
                  dismissOnOverlayPress={false}
                  modal
                  open={showPaymentSheet}
                  onOpenChange={setShowPaymentSheet}
                  snapPoints={[95]}
                  dismissOnSnapToBottom
                  animation="medium">
                  <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} backgroundColor="rgba(0,0,0,0.4)" />
                  <Sheet.Handle />
                  <Sheet.Frame bg="#F4F5F7" borderTopLeftRadius={24} borderTopRightRadius={24} padding="$5">
                    <XStack alignItems="center" justifyContent="space-between">
                      <Text fontWeight="bold">支付方式</Text>
                      <Button chromeless onPress={() => setShowPaymentSheet(false)}>
                        <RiCloseLine size={22} />
                      </Button>
                    </XStack>
                    <ScrollView showsVerticalScrollIndicator={false}>
                      <YStack className="gap-3 pb-8">
                        <Text>xxx</Text>
                      </YStack>
                    </ScrollView>
                  </Sheet.Frame>
                </Sheet>
              </RadiusCard>
              {/* 明細 */}
              <RadiusCard>
                <YStack gap="$3">
                  <YStack gap={4}>
                    <TextSpaceBetween title="儲值金額" price={`NT$ 1,000`} />
                    <TextSpaceBetween title="活動加贈" price={`+NT$ 50`} />
                  </YStack>
                  <View height={1} bg="$borderColor" />
                  <YStack gap={4}>
                    <XStack alignItems="center" justifyContent="space-between" mb={4}>
                      <Text fontWeight="bold">儲值後餘額</Text>
                      <Text fontSize={22} color={colors.primary} fontWeight="bold">
                        NT$ 3,050
                      </Text>
                    </XStack>
                  </YStack>
                </YStack>
              </RadiusCard>
            </YStack>
            <YStack backgroundColor={colors.bg} pt={8} style={{ position: "sticky", left: "50px", bottom: "0", width: "100%" }}>
              <XStack className="primary-bg-gradient" borderRadius={20} p={15} alignItems="center" justifyContent="center">
                <XStack gap={10} onPress={methods.handleSubmit(onSubmit, onInvalid)}>
                  <Text color="white" fontSize={16} fontWeight="bold">
                    確認儲值 1,000
                  </Text>
                </XStack>
              </XStack>
            </YStack>
          </Form>
        </FormProvider>
      </RadiusContentWrapper>
    </YStack>
  );
};

export default TopUp;
